import { useEffect, useState } from "react";
import { useController } from "react-hook-form";

import TextInput from "@/form/inputs/TextInput/TextInput";
import { useFieldIds } from "@/form/useFieldIds";
import { cn } from "@/lib/utils";

import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

type PhoneNumberFieldProps = {
  readonly className?: string;
  readonly disabled?: boolean;
  readonly name: string;
  readonly label: string;
  readonly placeholder?: string;
  readonly hint?: string;
  readonly hideValidation?: boolean;
  readonly hideLabel?: boolean;
};

export default function PhoneNumberField({
  disabled,
  name,
  label,
  className,
  placeholder,
  hint,
  hideValidation,
  hideLabel,
}: PhoneNumberFieldProps): React.ReactElement {
  const { inputId, labelId, errorId, describedBy, hintId } = useFieldIds(name);
  const { field, fieldState } = useController({
    name,
  });

  const [displayValue, setDisplayValue] = useState(
    field.value ? maskPhoneNumber(field.value) : ""
  );

  const invalid = !!fieldState.error;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setDisplayValue("");
      field.onChange("");
      return;
    }

    const rawValue = value.replaceAll(/\D/g, "");
    field.onChange(rawValue);

    const formatted = maskPhoneNumber(rawValue);
    setDisplayValue(formatted);
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value) {
      const rawValue = value.replaceAll(/\D/, "");
      setDisplayValue(maskPhoneNumber(rawValue));
    } else {
      setDisplayValue("");
    }
  };

  useEffect(() => {
    if (field?.value !== displayValue.replaceAll(/\D/g, "")) {
      setDisplayValue(maskPhoneNumber(field.value));
    }
  }, [field.value]);

  return (
    <div
      className={cn("PhoneNumberField grid gap-2")}
      data-testid="PhoneNumberField"
      data-field={name}
    >
      <div className={cn("grid", hideLabel && "sr-only")}>
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      </div>

      <TextInput
        id={inputId}
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
        aria-invalid={invalid}
        className={className}
        disabled={disabled}
        name={name}
        type="tel"
        placeholder={placeholder}
        value={displayValue}
        onBlur={handleBlur}
        onChange={handleChange}
        maxLength={14}
      />

      <div
        className={cn(
          "align-center flex h-3 justify-start",
          hideValidation && "hidden"
        )}
      >
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}

const maskPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return "";

  // Remove any existing formatting and non-digits
  const cleaned = phoneNumber.replaceAll(/\D/g, "");
  let formatted = "";

  // Add '(' at the start
  if (cleaned.length > 0) {
    formatted = "(" + cleaned;
  }

  // Add ') ' after third digit
  if (cleaned.length >= 4) {
    formatted = "(" + cleaned.substring(0, 3) + ") " + cleaned.substring(3, 6);
  }

  // Add '-' after sixth digit
  if (cleaned.length >= 7) {
    formatted =
      "(" +
      cleaned.substring(0, 3) +
      ") " +
      cleaned.substring(3, 6) +
      "-" +
      cleaned.substring(6, 10);
  }

  return formatted;
};
